import React, { useState } from "react";
import { useParams } from "react-router-dom";

// components
import DownloadButton from "../../../../../components.smart/DownloadButton/DownloadButton";
import ThumbSwitch from "../../../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import Pagination from "../../../../../components/Pagination";
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import Spacer from "../../../../../components/Spacer";
import ScrollableTable from "../../../../../components/TableLite/TableLiteScrollable";
import Typography from "../../../../../components/Text";
import AllFormsFilters, { AllFormsFilter, StatusSelectionOptions } from "./components/AllFormsFilters";
import AllFormsReadableFiltersList from "./components/AllFormsReadableFiltersList";
import getAllFormsTableCols from "./components/AllFormsTableCols";
import { generateCsvDownloadUrl } from "./components/generateCsvDownloadUrl";

// hooks
import { SortDirection, useSearchService, useSearchServicePrefetch } from "../../../../../api/hooks";

// services
import { SERVICE_FORMS } from "../../../../../api/services/defs/search-service";
import { ReviewForm } from "../../../../../api/services/searchServiceResultTypes/reviewForm";

// utils
import { getFiltersForSearchService } from "./utils";

// template
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import EmptySearchTableMessage from "../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import { CompanyReviewsNavigationLinks, REVIEWS_TABLE_LIMIT } from "../../constants";
// css
import css from "./AllForms.module.scss";

type AllFormsProps = {
	navLinks: SideNavigationLink[];
};

const AllForms = ({ navLinks }: AllFormsProps) => {
	const { page } = useParams<{ page: string }>();

	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<AllFormsFilter>({
		defaultSortName: "authors_due_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
		fallbackFilters: {
			redacted: false,
			status: [StatusSelectionOptions[0].value, StatusSelectionOptions[1].value, StatusSelectionOptions[2].value],
		},
	});

	const { data, isLoading } = useSearchService<ReviewForm>({
		service: SERVICE_FORMS,
		filters: getFiltersForSearchService(filters, debouncedSearchValue),
		skip: (pageNumber - 1) * REVIEWS_TABLE_LIMIT,
		limit: REVIEWS_TABLE_LIMIT,
		sort: sort,
		defaultErrorMessage: "There was an error getting Forms",
	});

	// prefetch bage before and 5 after
	useSearchServicePrefetch<ReviewForm>(
		{
			service: SERVICE_FORMS,
			filters: getFiltersForSearchService(filters, debouncedSearchValue),
			skip: (pageNumber - 2) * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort: sort,
		},
		pageNumber <= 1 ? 0 : 1
	);

	useSearchServicePrefetch<ReviewForm>(
		{
			service: SERVICE_FORMS,
			filters: getFiltersForSearchService(filters, debouncedSearchValue),
			skip: pageNumber * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort: sort,
		},
		5
	);

	const forms = data?.results || [];
	const totalForms = data?.total || 0;

	const additionalFiltersCount = Object.keys(filters).filter((key) => key !== "redacted").length;
	const emptyTableState = <EmptySearchTableMessage mainText="No Forms Found" />;

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => {
				setIsFiltersPanelOpen(true);
			}}
			searchPlaceholder="Search forms..."
			activeFiltersCount={additionalFiltersCount}
		/>
	);

	const toolbarInformationRow = <AllFormsReadableFiltersList filters={filters} setFilters={setFilters} />;

	const actionButtons = [
		<div className={css.showRedactedSwitch} key="show-redacted-button">
			<Typography weight="semi-bold" size="paragraph-3">
				Show Redacted
			</Typography>
			<ThumbSwitch
				value={filters.redacted || false}
				onChange={(value) => setFilters({ ...filters, redacted: value })}
			/>
		</div>,
		<a
			key="download-button"
			href={
				/**
				 * We are fake using the DownloadButton component,
				 * since no async_job exists for forms report csv.
				 * This gives the user a similar experience to
				 * other download csv buttons.
				 */
				generateCsvDownloadUrl(getFiltersForSearchService(filters, searchValue), sort)
			}
		>
			<DownloadButton onClick={() => {}} style={{ height: "32px" }}>
				Download CSV
			</DownloadButton>
		</a>,
	];

	const drawer = (
		<AllFormsFilters
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			filters={filters}
			onApplyFilters={(filters) => setFilters(filters)}
		/>
	);

	const mainContent = (
		<div>
			<ScrollableTable
				columns={getAllFormsTableCols()}
				rows={forms}
				isLoading={isLoading}
				activeSort={sort.name}
				activeSortDirection={sort.direction}
				onColumnClick={(columnID, nextDirection) =>
					setSort({ name: columnID, direction: nextDirection as SortDirection })
				}
				emptyChildren={emptyTableState}
			/>
			<Spacer height={2} />
			<Pagination
				pageCount={Math.ceil(totalForms / REVIEWS_TABLE_LIMIT)}
				currentPage={pageNumber}
				onClick={(page: number) => setPageNumber(page)}
			/>
		</div>
	);

	return (
		<InfoPageTemplate
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			actionButtons={actionButtons}
			mainContent={mainContent}
			drawer={drawer}
			navigationLinks={navLinks || CompanyReviewsNavigationLinks}
			activeNavigationLinkKey={page}
		/>
	);
};

export default AllForms;
